import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  api,
  formatMinor,
  normalizeRounds,
  type Balance,
  type DemoPlayer,
  type GameDef,
  type LaunchResult,
  type RoundSummary,
  type ServiceHealth,
  type ShowcaseConfig,
} from './api';
import { BRAND } from './config/brand';
import { previewClassForGameId } from './game-preview.js';

function useShowcase(): {
  config: ShowcaseConfig | null;
  players: DemoPlayer[];
  services: ServiceHealth[];
  error: string | null;
  reloadPlayers: () => void;
} {
  const [config, setConfig] = useState<ShowcaseConfig | null>(null);
  const [players, setPlayers] = useState<DemoPlayer[]>([]);
  const [services, setServices] = useState<ServiceHealth[]>([]);
  const [error, setError] = useState<string | null>(null);

  const reloadPlayers = useCallback(() => {
    api.listPlayers().then((r) => setPlayers(r.players)).catch((e: unknown) => setError(String(e)));
  }, []);

  useEffect(() => {
    api.getConfig().then(setConfig).catch((e: unknown) => setError(`Cannot reach showcase-control: ${String(e)}`));
    reloadPlayers();
  }, [reloadPlayers]);

  useEffect(() => {
    let active = true;
    const tick = (): void => {
      api.status().then((r) => { if (active) setServices(r.services); }).catch(() => undefined);
    };
    tick();
    const id = window.setInterval(tick, 10_000);
    return () => { active = false; window.clearInterval(id); };
  }, []);

  return { config, players, services, error, reloadPlayers };
}

export default function App(): JSX.Element {
  const { config, players, services, error, reloadPlayers } = useShowcase();
  const [selected, setSelected] = useState<DemoPlayer | null>(null);
  const [balance, setBalance] = useState<Balance | null>(null);
  const [rounds, setRounds] = useState<RoundSummary[]>([]);
  const [launch, setLaunch] = useState<LaunchResult | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const brand = config?.brand ?? { name: BRAND.name, tagline: BRAND.tagline, logo_text: BRAND.logoText };
  // The lobby renders one card per ENABLED game returned by showcase-control.
  // Enablement is never hardcoded here; an empty list shows no playable games.
  const games = config?.games ?? [];

  useEffect(() => {
    if (players.length > 0 && selected === null) {
      setSelected(players[0] ?? null);
    }
  }, [players, selected]);

  const refreshBalance = useCallback((slot: string) => {
    api.balance(slot).then(setBalance).catch(() => setBalance(null));
    api.recentRounds(slot).then((r) => setRounds(r.rounds)).catch(() => setRounds([]));
  }, []);

  useEffect(() => {
    if (selected) refreshBalance(selected.slot);
  }, [selected, refreshBalance]);

  // Listen for round/balance events posted by the embedded game.
  useEffect(() => {
    if (!launch || !selected) return undefined;
    let gameOrigin = '';
    try { gameOrigin = new URL(launch.origin).origin; } catch { gameOrigin = ''; }
    const slot = selected.slot;
    const handler = (ev: MessageEvent): void => {
      if (gameOrigin && ev.origin !== gameOrigin) return;
      const msg = ev.data as { type?: string; roundId?: string } | undefined;
      if (!msg || typeof msg.type !== 'string') return;
      if (msg.type === 'GAME_ROUND_SETTLED' && typeof msg.roundId === 'string') {
        api.observeRound(slot, msg.roundId).catch(() => undefined).finally(() => refreshBalance(slot));
      }
      if (msg.type === 'GAME_BALANCE') refreshBalance(slot);
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [launch, selected, refreshBalance]);

  const onPlay = useCallback(async (gameId: string) => {
    if (!selected) return;
    setBusy(`launch:${gameId}`);
    setNotice(null);
    try {
      const result = await api.launch(selected.slot, gameId);
      setLaunch(result);
    } catch (e: unknown) {
      setNotice(`Launch failed: ${String(e)}`);
    } finally {
      setBusy(null);
    }
  }, [selected]);

  const launchedGameName = useMemo(() => {
    const id = launch?.game_id ?? '';
    return games.find((g) => g.id === id)?.name ?? id ?? 'Game';
  }, [launch, games]);

  const onReset = useCallback(async () => {
    if (!selected) return;
    setBusy('reset');
    try {
      const r = await api.reset(selected.slot);
      setSelected(r.player);
      reloadPlayers();
      refreshBalance(r.player.slot);
      setNotice('Demo balance reset.');
    } catch (e: unknown) {
      setNotice(`Reset failed: ${String(e)}`);
    } finally {
      setBusy(null);
    }
  }, [selected, reloadPlayers, refreshBalance]);

  const urls = config?.urls ?? {};
  // Cross-links to the OTHER showcase front-ends come ONLY from the runtime
  // config served by showcase-control — never a baked-in origin. This keeps the
  // bundle free of localhost / internal hostnames; a link is shown only when the
  // control plane actually advertises that front-end's public URL.
  const backofficeURL = urls['backoffice'] ?? '';
  const operatorURL = urls['operator'] ?? '';
  const statusURL = urls['status'] ?? '';

  return (
    <div className="app">
      <DemoBadge indicator={config?.demo_indicator ?? 'VIRTUAL CREDITS'} />
      <header className="header glass">
        <div className="brand">
          <div className="logo-mark" aria-hidden>◇</div>
          <div>
            <div className="brand-name">{brand.name}</div>
            <div className="brand-tag">{brand.tagline}</div>
          </div>
        </div>
        <nav className="nav">
          {backofficeURL && <a href={backofficeURL} target="_blank" rel="noreferrer">Backoffice</a>}
          {operatorURL && <a href={operatorURL} target="_blank" rel="noreferrer">Operator Portal</a>}
          {statusURL && <a href={statusURL} target="_blank" rel="noreferrer">Status</a>}
          <span className="pill pill-demo">DEMO</span>
        </nav>
      </header>

      {error && <div className="banner banner-error">{error}</div>}
      {notice && <div className="banner banner-info" onAnimationEnd={() => setNotice(null)}>{notice}</div>}

      <main className="layout">
        <section className="games-section glass">
          <span className="kicker">Demo games</span>
          <h1>Choose a game</h1>
          <GamesGrid
            games={games}
            canPlay={selected !== null && busy === null}
            busyGameId={busy?.startsWith('launch:') ? busy.slice('launch:'.length) : null}
            onPlay={onPlay}
          />
        </section>

        <section className="panel glass">
          <h2>Demo player</h2>
          <PlayerSelector players={players} selected={selected} onSelect={setSelected} />
          <BalancePanel balance={balance} player={selected} onReset={onReset} busy={busy === 'reset'} />
          <CreatePlayer
            currencies={config?.currencies ?? ['USD']}
            languages={config?.languages ?? ['en']}
            onCreated={(p) => { reloadPlayers(); setSelected(p); }}
          />
        </section>

        <section className="panel glass">
          <h2>Recent rounds</h2>
          <RecentRounds rounds={rounds} backofficeURL={backofficeURL} slot={selected?.slot ?? ''} />
        </section>

        <section className="panel glass">
          <h2>Platform service status</h2>
          <ServiceStatus services={services} />
        </section>

        <Disclaimer items={config?.disclaimer ?? defaultDisclaimer} />
      </main>

      {launch && selected && (
        <GameModal
          launch={launch}
          gameName={launchedGameName}
          playerName={selected.display_name}
          onClose={() => { setLaunch(null); refreshBalance(selected.slot); }}
        />
      )}

      <footer className="footer">
        DEMO / VIRTUAL CREDITS ONLY — simulation and development environment. Not independently certified.
      </footer>
    </div>
  );
}

const defaultDisclaimer = [
  'Virtual credits only.',
  'No deposits.',
  'No withdrawals.',
  'No real-money gambling.',
  'Simulation and development environment.',
  'Game is not independently certified.',
];

function DemoBadge({ indicator }: { indicator: string }): JSX.Element {
  return <div className="demo-badge" role="status" aria-label="Demo mode">{indicator}</div>;
}

/** A simple generated preview tile per game (no remote asset dependency). */
function GamePreview({ gameId }: { gameId: string }): JSX.Element {
  const previewClass = previewClassForGameId(gameId);
  return (
    <div className={`game-preview game-preview--${previewClass}`} aria-hidden>
      {previewClass === 'dice' || previewClass === 'lightning-dice' ? (
        <>
          <span className="pip-die">⚄</span>
          <span className="pip-die">⚂</span>
          <span className="pip-die">⚅</span>
        </>
      ) : previewClass === 'seahorse-race' ? (
        <div className="seahorse-preview">
          <div className="seahorse-preview__lanes">
            <div className="seahorse-preview__lane"><span className="seahorse-preview__horse" /></div>
            <div className="seahorse-preview__lane seahorse-preview__lane--selected"><span className="seahorse-preview__horse" /></div>
            <div className="seahorse-preview__lane"><span className="seahorse-preview__horse" /></div>
          </div>
          <div className="seahorse-preview__bubbles" aria-hidden>
            <span /><span /><span /><span />
          </div>
        </div>
      ) : (
        <>
          <span className="reel">🍒</span>
          <span className="reel">🍋</span>
          <span className="reel">🔔</span>
        </>
      )}
    </div>
  );
}

export function GamesGrid({
  games,
  canPlay,
  busyGameId,
  onPlay,
}: {
  games: GameDef[];
  canPlay: boolean;
  busyGameId: string | null;
  onPlay: (gameId: string) => void;
}): JSX.Element {
  const safe = Array.isArray(games) ? games.filter((g) => g.enabled) : [];
  if (safe.length === 0) {
    return <p className="muted">No games are enabled for this operator.</p>;
  }
  return (
    <div className="games-grid">
      {safe.map((g) => (
        <article key={g.id} className="game-card glass" data-game-id={g.id}>
          <GamePreview gameId={g.id} />
          <div className="game-card-body">
            <h3 className="game-card-title">{g.name}</h3>
            <p className="game-card-desc">{g.description}</p>
            <div className="chips">
              <span className="chip">Volatility: {g.volatility}</span>
              <span className="chip">Mobile {g.mobile_support ? 'yes' : 'no'}</span>
              <span className="chip chip-sim" title={g.rtp_disclaimer}>
                RTP {g.declared_rtp} (simulation data)
              </span>
            </div>
            <button
              className="btn btn-primary"
              disabled={!canPlay}
              onClick={() => onPlay(g.id)}
              data-testid={`play-${g.id}`}
            >
              {busyGameId === g.id ? 'Launching…' : 'Play Demo'}
            </button>
          </div>
        </article>
      ))}
    </div>
  );
}

function PlayerSelector({
  players,
  selected,
  onSelect,
}: {
  players: DemoPlayer[];
  selected: DemoPlayer | null;
  onSelect: (p: DemoPlayer) => void;
}): JSX.Element {
  const safePlayers = Array.isArray(players) ? players : [];
  return (
    <div className="players">
      {safePlayers.map((p) => (
        <button
          key={p.slot}
          className={`player ${selected?.slot === p.slot ? 'player-active' : ''}`}
          onClick={() => onSelect(p)}
        >
          <span className="player-name">{p.display_name}</span>
          <span className="player-meta">
            {p.preset ? 'preset' : 'custom'} · {p.currency} · {formatMinor(p.starting_minor, p.currency)}
          </span>
        </button>
      ))}
      {safePlayers.length === 0 && <p className="muted">Loading demo players…</p>}
    </div>
  );
}

function BalancePanel({
  balance,
  player,
  onReset,
  busy,
}: {
  balance: Balance | null;
  player: DemoPlayer | null;
  onReset: () => void;
  busy: boolean;
}): JSX.Element {
  return (
    <div className="balance">
      <div className="balance-row">
        <span className="muted">Virtual balance</span>
        <span className="balance-value">
          {balance ? formatMinor(balance.balance_minor, balance.currency) : '—'}
        </span>
      </div>
      <button className="btn btn-ghost" disabled={!player || busy} onClick={onReset}>
        {busy ? 'Resetting…' : 'Reset Demo Balance'}
      </button>
    </div>
  );
}

function CreatePlayer({
  currencies,
  languages,
  onCreated,
}: {
  currencies: string[];
  languages: string[];
  onCreated: (p: DemoPlayer) => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [amount, setAmount] = useState('1000');
  const [currency, setCurrency] = useState(currencies[0] ?? 'USD');
  const [language, setLanguage] = useState(languages[0] ?? 'en');
  const [err, setErr] = useState<string | null>(null);

  const submit = (): void => {
    setErr(null);
    const minor = Math.round(Number(amount) * 100);
    if (!name.trim() || !Number.isFinite(minor) || minor <= 0) {
      setErr('Enter a name and a positive starting balance.');
      return;
    }
    api
      .createPlayer({ display_name: name.trim(), starting_balance_minor: minor, currency, language })
      .then((r) => { onCreated(r.player); setOpen(false); setName(''); })
      .catch((e: unknown) => setErr(String(e)));
  };

  if (!open) {
    return <button className="btn btn-ghost full" onClick={() => setOpen(true)}>+ Create custom demo player</button>;
  }
  return (
    <div className="create">
      <input className="input" placeholder="Display name" value={name} onChange={(e) => setName(e.target.value)} />
      <div className="create-row">
        <input className="input" type="number" min={1} placeholder="Starting balance" value={amount} onChange={(e) => setAmount(e.target.value)} />
        <select className="input" value={currency} onChange={(e) => setCurrency(e.target.value)}>
          {currencies.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select className="input" value={language} onChange={(e) => setLanguage(e.target.value)}>
          {languages.map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
      </div>
      {err && <p className="error-text">{err}</p>}
      <div className="create-row">
        <button className="btn btn-primary" onClick={submit}>Create</button>
        <button className="btn btn-ghost" onClick={() => setOpen(false)}>Cancel</button>
      </div>
    </div>
  );
}

export function RecentRounds({
  rounds,
  backofficeURL,
  slot,
}: {
  rounds: RoundSummary[] | null | undefined;
  backofficeURL: string;
  slot: string;
}): JSX.Element {
  // Normalize defensively: tolerate null/undefined/non-array and drop malformed
  // rows before touching .length / .map. Never assume the API gave us an array.
  const safeRounds = normalizeRounds(rounds);
  if (safeRounds.length === 0) {
    return <p className="muted">No demo rounds yet. Launch a game and complete a round.</p>;
  }
  return (
    <ul className="rounds">
      {safeRounds.map((r) => (
        <li key={r.round_id} className="round">
          <a href={`${backofficeURL}/rounds/${encodeURIComponent(r.round_id)}?slot=${encodeURIComponent(slot)}`} target="_blank" rel="noreferrer" className="round-id">
            {r.round_id.slice(0, 8)}…
          </a>
          <span className={`round-win ${r.total_win_minor > 0 ? 'win' : ''}`}>
            {formatMinor(r.total_win_minor, r.currency)}
          </span>
          <span className="muted">bet {formatMinor(r.total_bet_minor, r.currency)}</span>
          <span className="muted">{r.state}</span>
        </li>
      ))}
    </ul>
  );
}

function ServiceStatus({ services }: { services: ServiceHealth[] }): JSX.Element {
  const safeServices = Array.isArray(services) ? services : [];
  if (safeServices.length === 0) return <p className="muted">Checking services…</p>;
  return (
    <ul className="services">
      {safeServices.map((s) => (
        <li key={s.name} className="service">
          <span className={`dot ${s.status === 'up' ? 'dot-up' : 'dot-down'}`} />
          <span className="service-name">{s.name}</span>
          <span className="muted">{s.ready ? 'ready' : (s.error ?? 'not ready')}</span>
          {typeof s.latency_ms === 'number' && <span className="muted">{s.latency_ms}ms</span>}
        </li>
      ))}
    </ul>
  );
}

function Disclaimer({ items }: { items: string[] }): JSX.Element {
  const safeItems = Array.isArray(items) && items.length > 0 ? items : defaultDisclaimer;
  return (
    <section className="disclaimer glass">
      <strong>Demo disclaimer</strong>
      <ul>{safeItems.map((d) => <li key={d}>{d}</li>)}</ul>
    </section>
  );
}

/** True for phone/tablet-sized viewports where a top-level launch is preferred. */
function useIsNarrow(): boolean {
  const [narrow, setNarrow] = useState(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(max-width: 820px)').matches
      : false,
  );
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia('(max-width: 820px)');
    const onChange = (): void => setNarrow(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return narrow;
}

function GameModal({
  launch,
  gameName,
  playerName,
  onClose,
}: {
  launch: LaunchResult;
  gameName: string;
  playerName: string;
  onClose: () => void;
}): JSX.Element {
  const ref = useRef<HTMLIFrameElement | null>(null);
  const title = useMemo(() => `${gameName} — ${playerName} (DEMO)`, [gameName, playerName]);
  const narrow = useIsNarrow();

  // Opens the game at top level in a new tab. The launch URL carries the
  // single-use launch_token, gateway and parent params already minted by
  // showcase-control; we pass it straight through and NEVER log or persist it.
  // `noopener,noreferrer` severs the back-reference so the new top-level page
  // cannot script this lobby (and no token-bearing referrer leaks onward).
  const openFullscreen = useCallback(() => {
    window.open(launch.launch_url, '_blank', 'noopener,noreferrer');
  }, [launch.launch_url]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className={`modal glass ${narrow ? 'modal--narrow' : ''}`} onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <span className="modal-title">{title}</span>
          <span className="pill pill-demo">VIRTUAL CREDITS</span>
          <button className="btn btn-primary btn-fullscreen" onClick={openFullscreen}>
            Play fullscreen ↗
          </button>
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
        </div>
        {narrow ? (
          <div className="modal-fullscreen-cta">
            <p>For the best experience on this device, open the game in fullscreen.</p>
            <button className="btn btn-primary" onClick={openFullscreen}>Play fullscreen ↗</button>
            <details className="modal-embed-toggle">
              <summary>Or play embedded here</summary>
              <iframe
                ref={ref}
                title={title}
                className="game-frame"
                src={launch.launch_url}
                allow="autoplay; fullscreen"
                sandbox="allow-scripts allow-same-origin"
              />
            </details>
          </div>
        ) : (
          <iframe
            ref={ref}
            title={title}
            className="game-frame"
            src={launch.launch_url}
            allow="autoplay; fullscreen"
            sandbox="allow-scripts allow-same-origin"
          />
        )}
      </div>
    </div>
  );
}
